import Modal from 'components/Modal';

import useTranslation from 'pages/MaintenancePage/hooks/useTranslation';

import useReturnToService from './hooks/useReturnToService';
import useServiceDraft from './hooks/useServiceDraft';

import LogServiceFooter from './components/LogServiceFooter';
import LogServiceForm from './components/LogServiceForm';
import ReturnToServiceConfirm from './components/ReturnToServiceConfirm';
import ReturnToServiceFooter from './components/ReturnToServiceFooter';
import { TEST_ID } from './constants';
import type { FunctionComponent, LogService, Props } from './types';

import styles from './styles.module.scss';

const LogServicePanel: FunctionComponent<Props> = ({
    className = '',
    drone,
    isOpen,
    onClose,
    onSave,
    onSetAirworthy,
    serviceIntervals,
    testId = TEST_ID
}) => {
    const t = useTranslation();
    const serviceDraft = useServiceDraft({ drone, isOpen, serviceIntervals });

    // Wrap onSave so the draft hook marks its photo URLs as committed before the
    // next reseed would otherwise revoke them.
    const handleSaveRecord: LogService = params => {
        serviceDraft.handleDraftSaved();
        onSave(params);
    };

    const {
        handleCancelConfirm,
        handleConfirmKeep,
        handleConfirmReturn,
        handleRequestSave,
        pendingReadiness
    } = useReturnToService({ drone, isOpen, onSave: handleSaveRecord, onSetAirworthy });

    if (!drone) {
        return null;
    }

    // Held drones (grounded/hold) detour through the return-to-service confirm step;
    // ready drones save straight through.
    const handleSave = () => {
        const recordDraft = serviceDraft.buildDraft();

        if (!recordDraft) {
            return;
        }

        handleRequestSave({ draft: recordDraft });
    };

    const footer = pendingReadiness ? (
        <ReturnToServiceFooter
            onCancel={handleCancelConfirm}
            onKeepStatus={handleConfirmKeep}
            onReturnToService={handleConfirmReturn}
            readiness={pendingReadiness}
        />
    ) : (
        <LogServiceFooter
            isSaveDisabled={!serviceDraft.isDraftValid}
            onCancel={onClose}
            onSave={handleSave}
        />
    );

    return (
        <Modal
            className={`${styles.logServicePanel} ${className}`}
            footer={footer}
            isOpen={isOpen}
            onClose={onClose}
            overlayClassName={styles.logServiceOverlay}
            testId={testId}
            title={t.LOG_SERVICE_TITLE}>
            {pendingReadiness ? (
                <ReturnToServiceConfirm droneName={drone.name} readiness={pendingReadiness} />
            ) : (
                <LogServiceForm
                    draft={serviceDraft.draft}
                    drone={drone}
                    isRemainingLifeValid={serviceDraft.isRemainingLifeValid}
                    onAddPhotos={serviceDraft.handleAddPhotos}
                    onChangeCalibrationKind={serviceDraft.handleChangeCalibrationKind}
                    onChangeEventType={serviceDraft.handleChangeEventType}
                    onChangeNotes={serviceDraft.handleChangeNotes}
                    onChangePart={serviceDraft.handleChangePart}
                    onChangePartCondition={serviceDraft.handleChangePartCondition}
                    onChangePartSerialNumber={serviceDraft.handleChangePartSerialNumber}
                    onChangePerformedAt={serviceDraft.handleChangePerformedAt}
                    onChangeRemainingLife={serviceDraft.handleChangeRemainingLife}
                    onChangeUnit={serviceDraft.handleChangeUnit}
                    onRemovePhoto={serviceDraft.handleRemovePhoto}
                    serviceIntervals={serviceIntervals}
                />
            )}
        </Modal>
    );
};

export default LogServicePanel;
